export type GraphNode = {
  id: string;
  label: string;
  x: number;
  y: number;
  score: number;
  role: "core" | "complement" | "adjacent";
};

export type GraphEdge = {
  from: string;
  to: string;
  weight: number;
  reason: string;
};

export const offerGraph = {
  title: "Synthetic retail-bank offer graph",
  description:
    "Labeled synthetic example. Node radius follows a simple centrality score from co-occurrence and acceptance. Not a client graph and not a causal model.",
  nodes: [
    { id: "current", label: "Current account", x: 280, y: 180, score: 0.92, role: "core" },
    { id: "card", label: "Credit card", x: 140, y: 80, score: 0.71, role: "complement" },
    { id: "savings", label: "Savings", x: 430, y: 70, score: 0.64, role: "complement" },
    { id: "wallet", label: "Mobile wallet", x: 120, y: 280, score: 0.58, role: "adjacent" },
    { id: "loan", label: "Personal loan", x: 460, y: 270, score: 0.41, role: "adjacent" },
    { id: "insurance", label: "Insurance", x: 300, y: 340, score: 0.47, role: "complement" },
  ] satisfies GraphNode[],
  edges: [
    { from: "current", to: "card", weight: 0.86, reason: "Co-presented and often accepted together" },
    { from: "current", to: "savings", weight: 0.74, reason: "Same onboarding path" },
    { from: "current", to: "wallet", weight: 0.61, reason: "Digital-channel co-occurrence" },
    { from: "card", to: "loan", weight: 0.33, reason: "Occasional credit-need sequence" },
    { from: "savings", to: "insurance", weight: 0.48, reason: "Protection complement" },
    { from: "wallet", to: "insurance", weight: 0.29, reason: "Sparse; fallback territory" },
    { from: "current", to: "insurance", weight: 0.44, reason: "Bundle candidate" },
  ] satisfies GraphEdge[],
};

export function nodeById(id: string): GraphNode | undefined {
  return offerGraph.nodes.find((node) => node.id === id);
}

export function neighbors(id: string): { node: GraphNode; weight: number; reason: string }[] {
  return offerGraph.edges
    .filter((edge) => edge.from === id || edge.to === id)
    .map((edge) => {
      const otherId = edge.from === id ? edge.to : edge.from;
      const node = nodeById(otherId);
      if (!node) throw new Error(`Missing node ${otherId}`);
      return { node, weight: edge.weight, reason: edge.reason };
    })
    .sort((a, b) => b.weight - a.weight);
}
